import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import MuiExpansionPanel from '@material-ui/core/Accordion';
import MuiExpansionPanelSummary from '@material-ui/core/AccordionSummary';
import MuiExpansionPanelDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SupportAllowedTypeFields from '../../models/supportAllowedTypeFields';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import StyledCheckbox from './checkbox';
import RequestTypeField from '../../models/requestTypeField';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import HiddenRequestTypeFields from '../../models/hiddenRequestTypeFields';
import { neutral } from '../../theme/colors';

interface Props {
  typeFields: SupportAllowedTypeFields;
  uncheckedFields: HiddenRequestTypeFields;
  setUncheckedFields: React.Dispatch<React.SetStateAction<object[]>>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void; //Formik function to set the field value
  setField: string; //The field param used in the setFieldValue function call
}

const ExpansionPanel = withStyles({
  root: {
    border: `1px solid ${neutral[300]}`,
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0
    },
    '&:before': {
      display: 'none'
    },
    '&$expanded': {
      margin: 'auto'
    }
  },
  expanded: {}
})(MuiExpansionPanel);

const ExpansionPanelSummary = withStyles({
  root: {
    borderBottom: `1px solid ${neutral[300]}`,
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56
    }
  },
  content: {
    '&$expanded': {
      margin: '12px 0'
    }
  },
  expanded: {}
})(MuiExpansionPanelSummary);

const ExpansionPanelDetails = withStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiExpansionPanelDetails);

/**
 * @function SimpleExpansionPanel This is an expandable panel that contains a list of checkboxes.
 */
function SimpleExpansionPanel({ typeFields, uncheckedFields, setUncheckedFields, setFieldValue, setField }: Props) {
  const [checked, setChecked] = React.useState(
    typeFields.fields
      .map(field => field.id)
      .filter(field => {
        return uncheckedFields.value.indexOf(field) === -1;
      })
  );

  const handleToggle = (groupId: string, value: string) => () => {
    const typeField = typeFields.fields.find(field => field.id === value);
    if (typeField.required) {
      return;
    }
    const currentIndex = checked.indexOf(value);
    const newCheckedIds = [...checked];

    if (currentIndex === -1) {
      newCheckedIds.push(value);
    } else {
      newCheckedIds.splice(currentIndex, 1);
    }

    setChecked(newCheckedIds);
    updateUncheckedFields(groupId, typeFields, newCheckedIds);
  };

  const updateUncheckedFields = (groupId: string, typeFields: SupportAllowedTypeFields, newChecked: string[]) => {
    const allFields = typeFields.fields.map(field => field.id);
    const uncheckedFields = allFields.filter(field => !newChecked.includes(field));
    let newState: HiddenRequestTypeFields[];
    setUncheckedFields((prevState: HiddenRequestTypeFields[]) => {
      const groupIndex = prevState.findIndex((obj: HiddenRequestTypeFields) => {
        return obj.key === groupId;
      });
      newState = [...prevState];
      newState[groupIndex].value = uncheckedFields;
      return newState;
    });
    setFieldValue(setField, newState);
  };

  return (
    <ExpansionPanel>
      <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
        <h6>{typeFields.text}</h6>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails>
        <List dense role="list" style={{ width: '100%' }}>
          {typeFields.fields.map((field: RequestTypeField) => {
            const labelId = `transfer-list-item-${field.id}-label`;
            return (
              <ListItem
                key={field.id}
                role="listitem"
                button
                onClick={() => {
                  handleToggle(typeFields.value, field.id)();
                }}
              >
                <ListItemIcon>
                  <StyledCheckbox
                    checked={checked.indexOf(field.id) !== -1 || field.required}
                    disabled={field.required}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={field.name} />
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
}

const ExpansionList = React.memo(SimpleExpansionPanel);
export default ExpansionList;
